import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response.model';
import { ApiService } from 'src/app/core/services/api.service';
import { AddToCartModel } from '../models/user-cart.models';
import { IUserCart } from 'src/app/core/models/user-cart.model';
import { RatingSettings } from 'src/app/core/models/rating.model';

@Injectable({
  providedIn: 'root',
})
export class UserCartService {
  constructor(private readonly apiService: ApiService) {}

  private _cart: BehaviorSubject<IUserCart | null> = new BehaviorSubject<IUserCart | null>(null);

  get cart$(): Observable<IUserCart | null> {
    return this._cart.asObservable();
  }
  get cart(): IUserCart | null {
    return this._cart.value;
  }

  getCheckOutMethods(): Observable<ApiResponse<any>> {
    return this.apiService.get<ApiResponse<any>>('/checkout/CheckoutMethods');
  }

  getCart(): Observable<ApiResponse<IUserCart>> {
    return this.apiService.get<ApiResponse<IUserCart>>('/cart', {}).pipe(
      tap((res) => {
        if (res.data) {
          res.data.remainingToPay = Math.round(res.data.remainingToPay * 100) / 100;
          res.data.totalPriceWithTax = Math.round(res.data.totalPriceWithTax * 100) / 100;
        }
        this._cart.next(res.data);
      })
    );
  }

  checkRating(): Observable<ApiResponse<any>> {
    return this.apiService.get<ApiResponse<any>>('/reviews/checkRating');
  }

  submitRating(ratingSettings: RatingSettings): Observable<ApiResponse<any>> {
    return this.apiService.post<any, ApiResponse<any>>('/reviews', ratingSettings);
  }

  skipRating(id: number) {
    return this.apiService.post<any, ApiResponse<any>>('/reviews/skip/' + id, null);
  }

  getRatingSettings(id: number): Observable<ApiResponse<any>> {
    return this.apiService.get<ApiResponse<any>>('/reviews/rating/' + id);
  }

  setUserCart(cart: IUserCart | null): void {
    if (cart) {
      cart.remainingToPay = Math.round(cart.remainingToPay * 100) / 100;
      cart.totalPriceWithTax = Math.round(cart.totalPriceWithTax * 100) / 100;
    }
    this._cart.next(cart);
  }

  addToCart(model: AddToCartModel): Observable<ApiResponse<IUserCart>> {
    return this.apiService.post<AddToCartModel, ApiResponse<IUserCart>>('/cart', model).pipe(
      tap((res) => {
        this._cart.next(res.data);
      })
    );
  }

  deleteCartItem(id: number): Observable<ApiResponse<IUserCart>> {
    return this.apiService.delete<number, ApiResponse<IUserCart>>('/cart/', id).pipe(
      tap((res) => {
        this._cart.next(res.data);
      })
    );
  }

  clearCart(): Observable<ApiResponse<IUserCart>> {
    return this.apiService.delete<null, ApiResponse<IUserCart>>('/cart').pipe(
      tap((res) => {
        this._cart.next(res.data);
      })
    );
  }
}
